import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Assessment,
  AssessmentType,
  CreateAssessmentDTO,
  UpdateAssessmentDTO
} from '@promo/shared';
import { get, post, put, deleteFn } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for managing blinded & unblinded assessments operations
 */
export function useAssessments(packetId?: string, canViewAssessments: boolean = true) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const blindedKey = ['assessments', packetId, 'blinded'];
  const unblindedKey = ['assessments', packetId, 'unblinded'];
  const packetKey = ['packet', packetId];

  const upsertAssessment = (assessment: Assessment) => {
    const key = assessment.type === AssessmentType.UNBLINDED ? unblindedKey : blindedKey;
    queryClient.setQueryData<Assessment[]>(key, (current = []) => {
      const exists = current.some((item) => item.id === assessment.id);
      if (exists) {
        return current.map((item) => item.id === assessment.id ? assessment : item);
      }
      return [...current, assessment];
    });
  };

  // Fetch BLINDED assessments
  const blindedQuery = useQuery<Assessment[], Error>({
    queryKey: blindedKey,
    queryFn: () => (packetId ? get<Assessment[]>('/assessments', { packetId, type: AssessmentType.BLINDED }) : Promise.resolve([])),
    enabled: !!packetId && canViewAssessments,
  });

  // Fetch UNBLINDED assessments
  const unblindedQuery = useQuery<Assessment[], Error>({
    queryKey: unblindedKey,
    queryFn: () => (packetId ? get<Assessment[]>('/assessments', { packetId, type: AssessmentType.UNBLINDED }) : Promise.resolve([])),
    enabled: !!packetId && canViewAssessments,
  });

  // Mutations: create, update, delete
  const createMutation = useMutation<Assessment, Error, CreateAssessmentDTO>({
    mutationFn: (data) => post<Assessment>('/assessments', data),
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
    onSuccess: (assessment) => {
      upsertAssessment(assessment);
      queryClient.invalidateQueries({ queryKey: blindedKey });
      queryClient.invalidateQueries({ queryKey: unblindedKey });
      queryClient.invalidateQueries({ queryKey: packetKey });
      toast({ title: 'Success', description: 'Assessment created successfully' });
    }
  });

  const updateMutation = useMutation<Assessment, Error, { id: string; data: UpdateAssessmentDTO }>({
    mutationFn: ({ id, data }) => put<Assessment>(`/assessments/${id}`, data),
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
    onSuccess: (assessment) => {
      upsertAssessment(assessment);
      queryClient.invalidateQueries({ queryKey: blindedKey });
      queryClient.invalidateQueries({ queryKey: unblindedKey });
      queryClient.invalidateQueries({ queryKey: packetKey });
      toast({ title: 'Success', description: 'Assessment updated successfully' });
    }
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteFn<void>(`/assessments/${id}`),
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blindedKey });
      queryClient.invalidateQueries({ queryKey: unblindedKey });
      queryClient.invalidateQueries({ queryKey: packetKey });
      toast({ title: 'Success', description: 'Assessment deleted successfully' });
    }
  });

  return {
    // Lists
    blinded: blindedQuery.data || [],
    unblinded: unblindedQuery.data || [],
    isLoading: blindedQuery.isLoading || unblindedQuery.isLoading,
    error: blindedQuery.error || unblindedQuery.error,

    // Mutations
    createAssessment: createMutation.mutate,
    updateAssessment: (id: string, data: UpdateAssessmentDTO) => updateMutation.mutate({ id, data }),
    deleteAssessment: deleteMutation.mutate,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
